import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import { jobService as enqueueService } from "../services/jobService";
import { canOfferJobRetry } from "../lib/jobs/jobRetry";
import { useBroadcast } from "./useBroadcast";
import { userTopic } from "../lib/realtime/topics";
import { parseChange } from "../lib/realtime/payloads";

// Local canonical JobRecord type exported for consumers (panels, rows, config)
export interface JobRecord {
  id: string;
  type: string;
  label: string;
  /** jobs.user_id — absent sur une ligne optimiste. */
  userId?: string | null;
  scope?: string | null;
  createdAt: number;
  updatedAt: number;
  startedAt?: number | null;
  finishedAt?: number | null;
  phase?: string | null;
  phaseProgress?: number | null;
  status:
    | "pending"
    | "processing"
    | "chunked" // NEW
    | "merging" // NEW
    | "completed"
    | "failed"
    | "cancelled"
    | "paused"
    | "retrying";
  total?: number | null;
  completed?: number | null;
  artifactUrl?: string | null;
  error?: string | null;
  meta?: any;
  result?: {
    artifactUrl?: string;
    totalChunks?: number;
    [key: string]: any;
  } | null;
}

// NOTE: It's recommended to move this type to a shared `types.ts` file
// export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
// export interface JobRecord { ... }

interface JobCenterContextValue {
  jobs: Record<string, JobRecord>;
  ordered: JobRecord[]; // derived, sorted by created desc by default
  get: (id: string) => JobRecord | undefined;
  activeCount: number;
  isLoading: boolean;
  clearCompleted: () => Promise<void>;
  remove: (id: string) => Promise<void>;
  /**
   * Relance = nouvelle tâche (même type, libellé, payload) via enqueue-job,
   * puis suppression de l'ancienne. Rejette si la relance est refusée.
   */
  retry: (id: string) => Promise<void>;
  /** « Relancer » est-il proposé pour cette tâche ? */
  canRetry: (job: JobRecord) => boolean;
  stop: (id: string) => Promise<void>;
  optimisticAdd: (job: Partial<JobRecord> & { id: string }) => void;
  isClearing: boolean;
  deletingIds: Set<string>;
}

const JobCenterContext = createContext<JobCenterContextValue | undefined>(
  undefined
);

// Service functions to interact with the backend
const jobService = {
  getJobs: async (userId: string): Promise<JobRecord[]> => {
    const { data, error } = await supabase
      .from("jobs")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data.map(mapDbJob);
  },
  cancelJob: async (jobId: string) => {
    const { error } = await supabase.functions.invoke("cancel-job", { body: { jobId } });
    if (error) throw error;
  },
  deleteJob: async (jobId: string, userId: string) => {
    const { error } = await supabase
      .from("jobs")
      .delete()
      .match({ id: jobId, user_id: userId });
    if (error) throw error;
  },
  deleteCompletedJobs: async (userId: string) => {
    const { error } = await supabase
      .from("jobs")
      .delete()
      .match({ status: "completed", user_id: userId });
    if (error) throw error;
  },
};

function mapDbJob(dbJob: any): JobRecord {
  return {
    id: dbJob.id,
    type: dbJob.type,
    label: dbJob.label || "Unnamed Job",
    userId: dbJob.user_id ?? null,
    scope: dbJob.payload?.scope,
    createdAt: new Date(dbJob.created_at).getTime(),
    updatedAt: new Date(dbJob.updated_at).getTime(),
    startedAt: dbJob.started_at ? new Date(dbJob.started_at).getTime() : null,
    finishedAt: dbJob.finished_at ? new Date(dbJob.finished_at).getTime() : null,
    phase: dbJob.phase ?? null,
    phaseProgress: dbJob.phase_progress ?? null,
    status: dbJob.status,
    total: dbJob.total,
    completed: dbJob.progress,
    artifactUrl: dbJob.result?.artifactUrl,
    error: dbJob.error_message,
    meta: dbJob.payload,
    result: dbJob.result,
  };
}

const JOB_EVENTS = ["jobs_change"] as const;
const NO_TOPICS: readonly string[] = [];

/**
 * Applique un message `jobs_change` de user:<uid>. Ligne : ajoutée ou
 * remplacée (createdAt optimiste plus ancien conservé). Ping (DELETE, ou tâche
 * passée à un autre compte) : retirée par son `id`.
 */
export function applyJobChange(
  prev: Record<string, JobRecord>,
  payload: Record<string, unknown>
): Record<string, JobRecord> {
  const change = parseChange(payload);
  if (change.kind === "row") {
    const mapped = mapDbJob(change.record);
    const existing = prev[mapped.id];
    if (existing?.createdAt && existing.createdAt < mapped.createdAt) {
      mapped.createdAt = existing.createdAt;
    }
    return { ...prev, [mapped.id]: mapped };
  }
  if (!change.id || !prev[change.id]) return prev;
  const next = { ...prev };
  delete next[change.id];
  return next;
}

/**
 * État relu après une (re)jonction : la base fait foi ; les lignes
 * provisoires (sans userId, pas encore en base) restent jusqu'à leur réconciliation.
 */
export function mergeJobsSnapshot(
  prev: Record<string, JobRecord>,
  server: JobRecord[]
): Record<string, JobRecord> {
  const next: Record<string, JobRecord> = {};
  for (const job of server) {
    const existing = prev[job.id];
    next[job.id] =
      existing?.createdAt && existing.createdAt < job.createdAt
        ? { ...job, createdAt: existing.createdAt }
        : job;
  }
  for (const job of Object.values(prev)) {
    if (!next[job.id] && !job.userId) next[job.id] = job;
  }
  return next;
}

export const JobCenterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // NOTE: We removed direct notification context usage here to avoid provider ordering issues.
  // A separate component can consume JobCenter and Notification contexts together to emit toasts.
  const { user } = useAuth();

  const userId = user?.id;

  const { data: initial = [], isLoading, refetch } = useQuery({
    queryKey: ["jobs", userId],
    queryFn: () => jobService.getJobs(userId!),
    enabled: !!userId,
  });
  const [jobs, setJobs] = useState<Record<string, JobRecord>>({});
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [isClearing, setIsClearing] = useState(false);
  const hydratedRef = useRef(false);

  // Hydrate once when initial query resolves
  useEffect(() => {
    if (!hydratedRef.current && initial.length) {
      const map: Record<string, JobRecord> = {};
      initial.forEach((j) => {
        map[j.id] = j;
      });
      setJobs(map);
      hydratedRef.current = true;
    } else if (hydratedRef.current && initial.length === 0) {
      setJobs({});
      hydratedRef.current = true;
    }
  }, [initial]);

  useEffect(() => {
    if (!userId) {
      setJobs({});
      hydratedRef.current = false;
    }
  }, [userId]);

  // Temps réel : lignes `jobs` de user:<uid> (spec §5 ligne 35).
  const userTopics = useMemo(
    () => (userId ? [userTopic(userId)] : NO_TOPICS),
    [userId]
  );
  useBroadcast({
    topics: userTopics,
    isPrivate: true,
    ownerId: userId ?? null,
    events: JOB_EVENTS,
    onMessage: (_topic, _event, payload) => {
      setJobs((prev) => applyJobChange(prev, payload));
    },
    onResync: () => {
      void refetch().then((result) => {
        const server = result.data;
        if (!server) return;
        setJobs((prev) => mergeJobsSnapshot(prev, server));
        hydratedRef.current = true;
      });
    },
  });

  // Listen for optimistic enqueue events
  useEffect(() => {
    const addHandler = (e: any) => {
      const optimistic = e.detail as Partial<JobRecord> & { id: string };
      setJobs((prev) => {
        if (prev[optimistic.id]) return prev; // already present
        return { ...prev, [optimistic.id]: { ...(optimistic as JobRecord) } };
      });
    };
    const reconcileHandler = (e: any) => {
      const { placeholderId, record } = e.detail as {
        placeholderId: string;
        record: JobRecord;
      };
      setJobs((prev) => {
        const next = { ...prev };
        // If placeholder exists, delete and insert real
        if (next[placeholderId]) delete next[placeholderId];
        next[record.id] = record;
        return next;
      });
    };
    const failHandler = (e: any) => {
      const { id, error, drop } = e.detail as { id: string; error: string; drop?: boolean };
      setJobs((prev) => {
        if (!prev[id]) return prev;
        if (drop) {
          const next = { ...prev };
          delete next[id];
          return next;
        }
        return { ...prev, [id]: { ...prev[id], status: "failed", error } };
      });
    };
    if (typeof window !== "undefined") {
      window.addEventListener("job:enqueue", addHandler as any);
      window.addEventListener("job:enqueue:reconcile", reconcileHandler as any);
      window.addEventListener("job:enqueue:fail", failHandler as any);
      return () => {
        window.removeEventListener("job:enqueue", addHandler as any);
        window.removeEventListener(
          "job:enqueue:reconcile",
          reconcileHandler as any
        );
        window.removeEventListener("job:enqueue:fail", failHandler as any);
      };
    }
  }, []);

  const optimisticAdd = useCallback(
    (job: Partial<JobRecord> & { id: string }) => {
      setJobs((prev) => ({ ...prev, [job.id]: { ...(job as JobRecord) } }));
    },
    []
  );

  const remove = useCallback(
    async (id: string) => {
      setDeletingIds((ids) => new Set(ids).add(id));
      let removed: JobRecord | undefined;
      setJobs((prev) => {
        removed = prev[id];
        const next = { ...prev };
        delete next[id];
        return next;
      });
      if (!userId) {
        setDeletingIds((ids) => {
          const next = new Set(ids);
          next.delete(id);
          return next;
        });
        return;
      }
      try {
        await jobService.deleteJob(id, userId);
      } catch (e) {
        // Put the row back: a refused delete that leaves the list looking empty
        // reappears on the next reload and reads as the app losing track.
        setJobs((prev) => (removed ? { [removed.id]: removed, ...prev } : prev));
        setDeletingIds((ids) => {
          const next = new Set(ids);
          next.delete(id);
          return next;
        });
        throw e;
      }
      setDeletingIds((ids) => {
        const next = new Set(ids);
        next.delete(id);
        return next;
      });
    },
    [userId]
  );

  const clearCompleted = useCallback(async () => {
    setIsClearing(true);
    // Keep the removed rows so a refused delete can be put back. Dropping them
    // optimistically and swallowing the error is what made "Nettoyer" look like
    // it worked until the next reload brought every job back.
    let removed: Record<string, JobRecord> = {};
    setJobs((prev) => {
      const next: Record<string, JobRecord> = {};
      removed = {};
      Object.values(prev).forEach((job) => {
        if (job.status !== "completed") next[job.id] = job;
        else removed[job.id] = job;
      });
      return next;
    });
    if (!userId) {
      setIsClearing(false);
      return;
    }
    try {
      await jobService.deleteCompletedJobs(userId);
    } catch (e: any) {
      // Roll back and tell the caller. A single referenced job used to abort the
      // whole statement (FK RESTRICT from calendar_draws), so nothing was deleted.
      setJobs((prev) => ({ ...removed, ...prev }));
      setIsClearing(false);
      throw e;
    }
    setIsClearing(false);
  }, [userId]);

  const canRetry = useCallback(
    (job: JobRecord) => canOfferJobRetry(job, userId),
    [userId]
  );

  // La ligne d'origine n'est pas touchée avant que la nouvelle tâche existe :
  // un refus (droits, réseau) la laisse telle quelle, avec son erreur. La
  // nouvelle arrive par l'événement job:enqueue (ligne provisoire) puis le
  // realtime INSERT.
  const retry = useCallback(
    async (id: string) => {
      if (!userId) throw new Error("Not signed in");
      const { previousRemoved } = await enqueueService.retryJob(id, userId);
      if (previousRemoved) {
        setJobs((prev) => {
          if (!prev[id]) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    },
    [userId]
  );

  const stop = useCallback(async (id: string) => {
    setJobs((prev) => ({ ...prev, [id]: prev[id] ? { ...prev[id], status: "cancelled" as any } : prev[id] }));
    try { await jobService.cancelJob(id); } catch (e) { /* realtime will reconcile */ }
  }, []);

  const ordered = Object.values(jobs).sort((a, b) => b.createdAt - a.createdAt);
  const activeCount = ordered.filter((j) =>
    ["pending", "processing", "chunked", "merging", "retrying"].includes(
      j.status
    )
  ).length;

  const value: JobCenterContextValue = {
    jobs,
    ordered,
    get: (id) => jobs[id],
    activeCount,
    isLoading: isLoading && !hydratedRef.current,
    clearCompleted,
    remove,
    retry,
    canRetry,
    stop,
    optimisticAdd,
    isClearing,
    deletingIds,
  };

  return (
    <JobCenterContext.Provider value={value}>
      {children}
    </JobCenterContext.Provider>
  );
};

export function useJobCenter() {
  const ctx = useContext(JobCenterContext);
  if (!ctx)
    throw new Error("useJobCenter must be used within JobCenterProvider");
  return ctx;
}
